import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, timer, switchMap, takeWhile, share, filter } from 'rxjs';
import { environment } from '../../environments/environment';

export interface OrgTemplateAadStatus {
  id: string;
  templateId: string;
  status: 'Provisioning' | 'Active' | 'Failed' | 'Deleting' | 'Deleted';
  userPrincipalName: string | null;
  provisionedAt: string | null;
  errorMessage: string | null;
}

export interface OrgTemplateAadCredentials {
  userPrincipalName: string;
  temporaryAccessPass: string;
  azurePortalUrl: string;
}

@Injectable({ providedIn: 'root' })
export class OrgTemplateAadService {
  private readonly BASE = `${environment.apiUrl}/v1/org-template`;

  constructor(private http: HttpClient) {}

  triggerProvision(templateId: string): Observable<OrgTemplateAadStatus> {
    return this.http.post<OrgTemplateAadStatus>(`${this.BASE}/${templateId}/aad-user`, {});
  }

  getStatus(templateId: string): Observable<OrgTemplateAadStatus | null> {
    return this.http.get<OrgTemplateAadStatus>(`${this.BASE}/${templateId}/aad-user`);
  }

  /**
   * Polls the status endpoint every 3 seconds until the status reaches a terminal state
   * (Active, Failed, or Deleted). Emits each status including the final terminal one.
   */
  pollUntilTerminal(templateId: string): Observable<OrgTemplateAadStatus> {
    const isTerminal = (s: string) => s === 'Active' || s === 'Failed' || s === 'Deleted';
    return timer(0, 3000).pipe(
      switchMap(() => this.getStatus(templateId)),
      // Narrow out null so takeWhile receives a non-nullable type.
      filter((s): s is OrgTemplateAadStatus => s !== null),
      takeWhile(s => !isTerminal(s.status), true),
      share()
    );
  }

  triggerDelete(templateId: string): Observable<void> {
    return this.http.delete<void>(`${this.BASE}/${templateId}/aad-user`);
  }

  getCredentials(templateId: string): Observable<OrgTemplateAadCredentials> {
    return this.http.get<OrgTemplateAadCredentials>(`${this.BASE}/${templateId}/aad-user/credentials`);
  }

  regenerateTap(templateId: string): Observable<{ temporaryAccessPass: string }> {
    return this.http.post<{ temporaryAccessPass: string }>(
      `${this.BASE}/${templateId}/aad-user/regenerate-tap`, {});
  }

  /**
   * Returns the AAD credentials (UPN + TAP) for a class-scoped (customer-template) AAD user.
   * These are stored per-class in CustomerCloudIds[classId] rather than the shared
   * org-template Data.AadProvisioning record.
   */
  getClassTemplateCredentials(templateId: string, classId: string): Observable<OrgTemplateAadCredentials> {
    return this.http.get<OrgTemplateAadCredentials>(
      `${this.BASE}/${templateId}/aad-user/class/${classId}/credentials`);
  }

  /**
   * Regenerates the Temporary Access Pass for a class-scoped Active AAD user.
   * Updates CustomerCloudIds[classId]["tap"] in the resource document.
   */
  regenerateClassTemplateTap(templateId: string, classId: string): Observable<{ temporaryAccessPass: string }> {
    return this.http.post<{ temporaryAccessPass: string }>(
      `${this.BASE}/${templateId}/aad-user/class/${classId}/regenerate-tap`, {});
  }
}
